/**
 * Festival Card Component
 * Individual festival card with expand/collapse functionality
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import {
  typographyColors,
  backgroundColors,
  spacing,
  borders,
  typography,
  fontPrimary,
  strokeColors,
} from '@/design-system/utils';
import { ShowMoreTextButton } from '@/components/ui/show-more-text-button';
import { useTextOverflow } from '@/hooks/useTextOverflow';

export interface Festival {
  title: string;
  description: string;
  longDescription: string;
  imageUrls: string[];
}

export interface FestivalCardProps {
  festival: Festival;
  onExpand?: (festivalTitle: string) => void;
  index?: number;
}

export const FestivalCard: React.FC<FestivalCardProps> = ({
  festival,
  onExpand,
  index = 0,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { textRef, exceedsLines } = useTextOverflow<HTMLParagraphElement>(
    3,
    festival.description
  );

  // Show show more/less if either text overflows OR there's a long description
  const shouldShowMoreButton = exceedsLines || festival.longDescription;

  // Debug logging to check show more/less functionality
  console.log('🎭 [FestivalCard] Show More/Less Debug:', {
    festivalTitle: festival.title,
    descriptionLength: festival.description?.length || 0,
    descriptionPreview: festival.description?.substring(0, 100) + '...',
    exceedsLines,
    isExpanded,
    hasLongDescription: !!festival.longDescription,
    longDescriptionLength: festival.longDescription?.length || 0,
    shouldShowMoreButton,
  });

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    if (onExpand) {
      onExpand(festival.title);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay: 0.4 + index * 0.08,
        ease: [0.4, 0, 0.2, 1],
        opacity: { duration: 0.4 },
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[2], // 8px between festival cards
      }}
    >
      <motion.div
        initial={{ scale: 0.98, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          duration: 0.4,
          ease: [0.4, 0, 0.2, 1],
        }}
        whileHover={{
          scale: 1.01,
          transition: {
            duration: 0.2,
            ease: [0.4, 0, 0.2, 1],
          },
        }}
        style={{
          backgroundColor: backgroundColors.primary,
          border: `${borders.width.thin} solid ${strokeColors.lightest}`,
          borderRadius: borders.radius.lg, // 8-10px radius
          padding: spacing[3], // 12px
          display: 'flex',
          gap: spacing[3], // 12px gap between image and text
          alignItems: 'flex-start',
        }}
      >
        {/* Image Section - Left side, ~1/3 width; placeholder when no images */}
        <div
          style={{
            position: 'relative',
            width: 174,
            height: 113,
            borderRadius: borders.radius.lg,
            overflow: 'hidden',
            flexShrink: 0,
          }}
          className='w-[120px] h-[78px] md:w-[174px] md:h-[113px]'
        >
          <Image
            src={
              Array.isArray(festival.imageUrls) && festival.imageUrls[0]
                ? festival.imageUrls[0]
                : '/assets/images/No-Image.jpg'
            }
            alt={festival.title}
            fill
            style={{ objectFit: 'cover' }}
          />
        </div>

        {/* Text Section - Right side, ~2/3 width */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[2], // 8px gap between title and description
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* Title */}
          <p
            style={{
              fontFamily: fontPrimary,
              fontSize: typography.size.lg,
              fontWeight: typography.weight.semibold,
              color: typographyColors.primary,
              margin: 0,
            }}
            className='md:text-xl'
          >
            {festival.title}
          </p>

          {/* Description */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[1],
              flex: 1,
            }}
          >
            <p
              ref={textRef}
              style={{
                fontFamily: fontPrimary,
                fontSize: typography.size.sm,
                fontWeight: typography.weight.regular,
                color: typographyColors.primary,
                lineHeight: typography.lineHeight.relaxed,
                margin: 0,
                display: '-webkit-box',
                WebkitLineClamp: isExpanded ? 'none' : 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {festival.description}
            </p>

            {/* Long Description (only shown when expanded) */}
            {isExpanded && festival.longDescription && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  duration: 0.3,
                  ease: [0.4, 0, 0.2, 1],
                }}
                style={{
                  fontFamily: fontPrimary,
                  fontSize: typography.size.sm,
                  fontWeight: typography.weight.regular,
                  color: typographyColors.primary,
                  lineHeight: typography.lineHeight.relaxed,
                  margin: 0,
                  marginTop: spacing[2],
                  whiteSpace: 'pre-wrap',
                }}
              >
                {festival.longDescription}
              </motion.p>
            )}

            {/* Show More/Less Link - Bottom left of text section - show if text overflows OR has long description */}
            {shouldShowMoreButton && (
              <ShowMoreTextButton
                isExpanded={isExpanded}
                onClick={handleToggle}
              />
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
